<script>
import Header from './Header.vue';
import ScrollToTop from './ScrollToTop.vue'
export default {
  components: {
    'header-component': Header,
    ScrollToTop
  },
  mounted() {
      window.addEventListener('scroll', this.handleScroll);
    },
    methods: {
      handleScroll: function () {
        if (this.scTimer) return;
        this.scTimer = setTimeout(() => {
          this.scY = window.scrollY;
          clearTimeout(this.scTimer);
          this.scTimer = 0;
        }, 100);
      },
      toTop: function () {
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      },

      /* VIDEOS */
      playVideo(url, title){
        let video = document.getElementById("playVideos");
        let videoTitle = document.getElementById("video-title");

        video.src=url;
        videoTitle.innerHTML=title;
        //video.play();
        //video.focus();

      },
      openVideo() {
        let video = document.getElementById("playVideos");
        let videoOverlay = document.getElementById('video-overlay');
        let videoWrap = document.getElementById('video-wrapper');

        if (videoWrap.style.display == "none") {
          videoWrap.style.display = "inline-block";
          videoOverlay.style.display = "block";
        }
      },
      closeVideo() {
        let video = document.getElementById("playVideos");
        let videoOverlay = document.getElementById('video-overlay');
        let videoWrap = document.getElementById('video-wrapper');

        if (videoWrap.style.display == "inline-block") {
          videoWrap.style.display = "none";
          videoOverlay.style.display = "none";
          video.src="";
          //video.pause();
        }
      },
    },
}
</script>
<template>
  <div role="main">
    <div class="top-wrap">
      <div class="top-content">
        <div class="top-banner">
          <header-component></header-component>

          <h1>Expanding Internal Awareness</h1>
          <h2><i>Evaluate your internal processes and educate your internal staff</i></h2>

          <section>
            <p>
            Disease and treatment management incorporates multiple team members to help manage patients with chronic conditions.<sup>1-3</sup>
            Therefore, it is important to ensure that these team members have the knowledge and expertise to support adult patients with schizophrenia. The information on this page can be used to help support and educate your internal staff.
            </p>
          </section>

        </div>
      </div>

      <div class="bottom-banner">
        <p>Organizational Process Mapping</p>
        <p class="bottom-banner-text">
          Process mapping helps organizations illustrate the details of practices and identify opportunities for improvement.<sup>4,5</sup> The activity of reviewing the steps and their sequence as to who performs each step and how efficiently the process works can play a fundamental role in improving the quality of care delivered and operational efficiency.<sup>4,5</sup>
        </p>
        <p class="bottom-banner-text">
          According to faculty members at the Institute for Healthcare Improvement: “As healthcare systems try to adapt their processes to deliver the same level of excellent care to every patient, every time, they need tools like process mapping to objectively analyze their current processes.”<sup>5</sup>
        </p>
        <p class="bottom-banner-text">
          Process mapping could be used to evaluate your organization’s transition of care process for adults with schizophrenia. For example, the general steps in process mapping an organization’s transition of care practice would be<sup>4</sup>:
        </p>

        <div class="steps">

          <div class="step-item">
            <div class="step-circle">Step 1</div>
            <div class="step-item-desc-wrap">
              <p class="step-title">Identify the Process</p>
              <p class="step-desc">Identify a process to evaluate adult patients with schizophrenia who might be appropriate for long-acting injectable antipsychotic (LAI) treatment at discharge</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-circle">Step 2</div>
            <div class="step-item-desc-wrap">
              <p class="step-title">Map the Process</p>
              <p class="step-desc">Map the current care management protocol in sequence (eg, medication reconciliation, discharge process, follow-up care)</p>
              <p class="step-desc">Map the key processes that are marked for improvement (eg, follow-up appointments scheduled prior to discharge)</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-circle">Step 3</div>
            <div class="step-item-desc-wrap">
              <p class="step-title">Analyze the Process</p>
              <p class="step-desc">Evaluate which team members should be involved</p>
              <p class="step-desc dash">Assess the problems for the staff/patient/provider and opportunities for improvement (eg, access to timely electronic health records and follow-up appointments)</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-circle">Step 4</div>
            <div class="step-item-desc-wrap">
              <p class="step-title">Redefine the Process</p>
              <p class="step-desc">Redefine a process that recognizes adult patients with schizophrenia who might be appropriate for LAI treatment at discharge and ensure care coordination among providers</p>
              <p class="step-desc dash">The goal is to streamline the identification process while maximizing healthcare members’ strengths and availability <br>(eg, specialty pharmacy-led programs)</p>
            </div>
          </div>

        </div>

        <p><strong>A successful process mapping exercise should reveal</strong><sup class="sup-title">6</sup>:</p>
        <ul class="mapping-list">
          <li>Unnecessary steps, handovers, delays</li>
          <li>Any waste and/or duplication of efforts</li>
          <li>Tasks that do not add value to the patient journey</li>
          <li>Bottlenecks and constraints</li>
          <li>Potential to create safer patient care</li>
          <li>Understanding of the patient experience</li>
          <li>Where further analysis is required</li>
        </ul>

      </div>
    </div>

    <div class="body">
      <p class="subhead">Organizational Training on Schizophrenia Management</p>

      <p>
        Schizophrenia causes significant health and socioeconomic challenges for individuals with the disease, their family members, caregivers, and communities at large.<sup>7-9</sup> Due to the nature of schizophrenia, adherence to oral antipsychotic medications remains a primary challenge for those with the disease.<sup>10</sup> LAIs may be able to address this challenge.<sup>11</sup> Recent clinical guidance from national organizations and state-based payers supports the use of LAIs in adult patients with schizophrenia.<sup>12-15</sup> In fact, the National Council of Mental Wellbeing* supports the earlier use of LAIs in the treatment journey.<sup>13</sup>
      </p>
      <p>
        The resources below will help your staff better understand the role LAI medications could play in initiatives targeted to delay relapse in adult patients with schizophrenia.
      </p>

       <p class="footnote">
        *Formerly known as the National Council for Behavioral Health.
      </p>

      <div class="body-list-wrap">


        <div class="body-list-item">
          <div class="web-icon">The State of Schizophrenia</div>
          <p>Tune in to a discussion with clinicians about the stigma and burden of schizophrenia.</p>
          <a href="https://talkinglais.com/reducing-series-ep1" aria-label="Visit The State of Schizophrenia website" target="_blank"  class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>

        <div class="body-list-item">
          <div class="web-icon">The Cost of  Repeated Relapse</div>
          <p>Hear what a panel of clinicians says about the consequences of repeated relapse in adult patients with schizophrenia, such as loss of work.</p>
          <a href="https://talkinglais.com/reducing-series-ep2" aria-label="Visit The Cost of Repeated Relapse website" target="_blank"  class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>
        <div class="body-list-item">
          <div class="web-icon">Benefits of LAI Intervention</div>
          <p>Listen to a discussion with clinicians about biases and outcomes when evaluating treatment with LAI intervention for adult patients with schizophrenia.</p>
          <a href="https://talkinglais.com/reducing-series-ep3" aria-label="Visit Benefits of LAI Intervention website" target="_blank"  class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>
        <div class="body-list-item">
          <div class="web-icon">Why Are LAIs Underutilized?</div>
          <p>Attend a broadcast of clinicians detailing why treatment with LAIs is underutilized (eg, stigma, lack of awareness, or access) in adult patients with schizophrenia.</p>
          <a href="https://talkinglais.com/reducing-series-ep4" aria-label="Visit Why Are LAIs Underutilized website" target="_blank"  class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>

        <div class="body-list-item">
          <div class="web-icon">Improving How HCPs Offer Patients LAIs</div>
          <p>Learn how practicing clinicians proactively approach the conversation around treatment with LAIs with adult patients with schizophrenia.</p>
          <a href="https://talkinglais.com/reducing-series-ep5" aria-label="Visit Improving How HCPs Offer Patients LAIs website" target="_blank" class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>
        <div class="body-list-item">
          <div class="web-icon">Importance of the Therapeutic Alliance</div>
          <p>Understand how to overcome potential roadblocks to treatment with LAIs and the importance of the Therapeutic Alliance and caregiver engagement.</p>
          <a href="https://talkinglais.com/reducing-series-ep6" aria-label="Visit Importance of the Therapeutic Alliance website" target="_blank" class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>
        <div class="body-list-item">
          <div class="web-icon">Shared Decision Making and LAIs</div>
          <p>Discuss the shared decision-making process and considerations for successful dialogue around treatment with LAIs.</p>
          <a href="https://talkinglais.com/reducing-series-ep7" aria-label="Visit Shared Decision Making and LAIs website" target="_blank" class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>

        <div class="body-list-item">
          <div class="no-icon">Facilitating the Discussion on LAIs</div>
          <p>Through mock conversations, learn how team members can discuss LAI treatment with an adult patient with schizophrenia.</p>
          <div class="video-thumb" tabindex="0" role="button" aria-label="Facilitating the Discussion on LAIs"
            @click="playVideo('https://players.brightcove.net/4317630935001/default_default/index.html?videoId=6266522890001', 'Facilitating the Discussion on LAIs'); openVideo();"
            @keydown.enter.prevent="playVideo('https://players.brightcove.net/4317630935001/default_default/index.html?videoId=6266522890001', 'Facilitating the Discussion on LAIs'); openVideo();"
            @keydown.space.prevent="playVideo('https://players.brightcove.net/4317630935001/default_default/index.html?videoId=6266522890001', 'Facilitating the Discussion on LAIs'); openVideo();">
            <img src="../img/Facilitating_the_Discussion_on_LAIs.png" alt="Facilitating the Discussion on Long-Acting Injectable Antipsychotics" />
          </div>
        </div>

        <div class="body-list-item">
          <div class="web-icon">How to Give<br>LAIs</div>
          <p>A review for nurses and other psychiatric healthcare professionals about how to give LAI intramuscular injections.</p>
          <a href="https://www.janssenmd.com/intramuscularinjections" aria-label="Visit How to Give LAIs website" target="_blank" class="button visit">VISIT SITE <img src="../img/visit-icon.png" alt=""/></a>
        </div>

        <ScrollToTop />

        <div class="video-wrapper video-closed" id="video-wrapper" style="display:none;">
          <div class="video-close" id="video-close"  @click="closeVideo();">X</div>
          <div class="video-title" id="video-title">Video Title</div>
          <iframe id="playVideos" src="" allowfullscreen="" allow="encrypted-media" style="top: 0px; right: 0px; bottom: 0px; left: 0px; width: 100%; height: 90%;"></iframe>
          <!--<video id="playVideos" controls disablePictureInPicture>
            <source id="playVideosss"  type="video/mp4">
          </video>-->
        </div>
        <div id="video-overlay" class="overlay" @click="closeVideo();"></div>
      </div>

      <p class="references footer-references">
        <strong>References: 1.</strong> Academy of Managed Care Pharmacy. Disease Management. July 17, 2019. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://www.amcp.org/about/managed-care-pharmacy-101/concepts-managed-care-pharmacy/disease-management" target="_blank">https://www.amcp.org/about/managed-care-pharmacy-101/concepts-managed-care-pharmacy/disease-management</a></span> <strong>2.</strong> Gelber S, Dougherty RH. <i>Disease Management for Chronic Behavioral Health and Substance Use Disorders.</i> Center for Health Care Strategies, The Robert Wood Johnson Foundation’s Medicaid Managed Care
Program. 2005:1-31. <strong>3.</strong> Georgetown University; Health Policy Institute. Disease management programs: improving health while reducing costs? Published July 2, 2020. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://hpi.georgetown.edu/management/" target="_blank">https://hpi.georgetown.edu/management/</a></span> <strong>4.</strong> Heher YK, Chen Y. Process mapping: a cornerstone of quality improvement. <i>Cancer Cytopathol</i>. 2017;125(12):887-890. doi:10.1002/cncy.21946 <strong>5.</strong> IHI Multimedia Team. 5 steps for creating value through process mapping and observation. Institute for Healthcare Improvement. Published October 1, 2015. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://www.ihi.org/insights/5-steps-creating-value-through-process-mapping-and-observation" target="_blank">https://www.ihi.org/insights/5-steps-creating-value-through-process-mapping-and-observation</a></span> <strong>6.</strong> Trebble TM, Hansi N, Hydes T, Smith MA, Baker M. Process mapping the patient journey: an introduction. <i>BMJ</i>. 2010;341:c4078. doi:10.1136/bmj.c4078 <strong>7.</strong> US Department of Health and Human Services; National Institutes of Health; The National Institute of Mental Health. Schizophrenia. NIH publication No. 21-MH-8082. Revised 2021. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://www.nimh.nih.gov/sites/default/files/documents/health/publications/schizophrenia/schizophrenia_1.pdf" target="_blank">https://www.nimh.nih.gov/sites/default/files/documents/health/publications/schizophrenia/schizophrenia_1.pdf</a></span> <strong>8.</strong> The Treatment Advocacy Center. Schizophrenia – Fact Sheet. August 13, 2008. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://www.treatmentadvocacycenter.org/evidence-and-research/learn-more-about/25-schizophrenia-fact-sheet" target="_blank">https://www.treatmentadvocacycenter.org/evidence-and-research/learn-more-about/25-schizophrenia-fact-sheet</a></span> <strong>9.</strong> Cloutier M, Aigbogun MS, Guerin A, et al. The economic burden of schizophrenia in the United States in 2013. <i>J Clin Psychiatry</i>. 2016;77(6):764-771. <span>doi:10.4088/JCP.15m10278</span> <strong>10.</strong> Haddad PM, Brain C, Scott J. Nonadherence with antipsychotic medication in schizophrenia: challenges and management strategies. <i> Patient Relat Outcome Meas</i>. 2014;5:43-62. doi:10.2147/PROM.S42735 <strong>11.</strong> Marcus SC, Zummo J, Pettit AR, et al. Antipsychotic adherence and rehospitalization in schizophrenia patients receiving oral versus long-acting injectable antipsychotics following hospital discharge. <i>J Manag Care Spec Pharm</i>. 2015;21(9):754-768. doi:<span>10.18553/jmcp.2015.21.9.754</span> <strong>12. </strong> Keepers GA, Fochtmann LJ, Anzia JM, et al. <i>The American Psychiatric Association Practice Guideline for the Treatment of Patients With Schizophrenia</i>. Third edition. American Psychiatric Association; 2021. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://psychiatryonline.org/doi/pdf/10.1176/appi.books.9780890424841" target="_blank">https://psychiatryonline.org/doi/pdf/10.1176/appi.books.9780890424841</a></span> <strong>13.</strong> National Council For Mental Wellbeing. <i>Guide to Long-Acting Medications for Clinicians and Organizations</i>. Published February 2, 2022. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://www.thenationalcouncil.org/wp-content/uploads/2021/12/2022.02.02_NC_Updated-Guide-To-LAMs.pdf" target="_blank">https://www.thenationalcouncil.org/wp-content/uploads/2021/12/2022.02.02_NC_Updated-Guide-To-LAMs.pdf</a></span> <strong>14.</strong> University of South Florida; Florida Medicaid Drug Therapy Management Program for Behavioral Health. 2019–2020 Florida best practice psychotherapeutic medication guidelines for adults. January 2020. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://floridabhcenter.cbcs.usf.edu/wp-content/uploads/2021/04/2019-Psychotherapeutic-Medication-Guidelines-for-Adults-with-References_06-04-20.pdf" target="_blank">https://floridabhcenter.cbcs.usf.edu/wp-content/uploads/2021/04/2019-Psychotherapeutic-Medication-Guidelines-for-Adults-with-References_06-04-20.pdf</a></span> <strong>15.</strong> Mental Health Clinical Advisory Group. Mental health care guide for licensed practitioners and mental health professionals: schizophrenia. Oregon Health Authority; OHA 7548. Published March 2019. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://www.oregon.gov/oha/HSD/OHP/Documents/Schizophrenia%20Care%20Guide%20March%202019.pdf" target="_blank">https://www.oregon.gov/oha/HSD/OHP/Documents/Schizophrenia%20Care%20Guide%20March%202019.pdf</a></span>
      </p>
    </div>
  </div>

</template>

<style scoped>
#app>div {
  width: 100%;
}

/*TOP */

.top-wrap {
  background: rgb(0, 18, 82);
  background: linear-gradient(0deg, rgba(0, 18, 82, 1) 0%, rgba(0, 70, 126, 1) 50%, rgba(0, 70, 126, 1) 100%);
  color: #ffffff;
  width: 100%;
}

.top-content {
  background: linear-gradient(to top, #0079A8, #0084B8);
  border-radius: 0 0 0 100px;
  /*height: 539px;*/
  margin-left: 3%;
  width: 97%;
}

.top-banner {
  background: url(../img/banner-bg.png) no-repeat 0 0;
  background-size: cover;
  border-radius: 0 0 0 100px;
  /*height: 483px;*/
  margin-left: 1.5%;
  margin-bottom: 56px;
  padding-bottom: 78px;
  width: 98.5%;
}

.top-banner h1 {
  display: block;
  font-family: "Gotham", sans-serif;
  font-size: 36px;
  font-weight: 700;
  line-height: 36px;
  margin: 68px auto 24px auto;
  max-width: 1121px;
  text-align: center;
}

.top-banner h2 {
  display: block;
  font-family: "GothamBook", sans-serif;
  font-size: 22px;
  font-style: italic;
  font-weight: 400;
  line-height: 24px;
  margin: 0 auto;
  max-width: 1121px;
  text-align: center;
}

.top-banner section {
  font-family: "GothamBook", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  margin: 36px auto 0 auto;
  text-align: center;
  max-width: 1121px;
}

.top-banner section p {
  margin: 1em auto;
}


.top-content p.banner-footer {
  font-family: GothamBook, sans-serif;
  font-size: 24px;
  text-align: center;
}

.bottom-banner {
  color: #FFFFFF;
  display: block;
  font-family: GothamBook, sans-serif;
  font-size: 20px;
  font-weight: 700;
  margin: 70px auto 0 auto;
  text-align: left;
  width: 1119px;
}

.bottom-banner-text {
  color: #ffffff;
  font-family: GothamBook, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 22px;
}

.list-content {
  background: #eeeef3;
  margin: 0 auto;
  width: 100%;
}

.list-wrap {
  background: #eeeef3;
  display: block;
  margin: 65px auto;
  width: 1121px;
}

.list-item {
  background-color: #ffffff;
  border-radius: 0 35px 0 35px;
  box-shadow: 10px -5px 30px 0px rgba(0, 0, 0, 0.15);
  -moz-box-shadow: 10px -5px 30px 0px rgba(0, 0, 0, 0.15);
  -webkit-box-shadow: 10px -5px 30px 0px rgba(0, 0, 0, 0.15);
  height: 400px;
  margin: 0 34px 78px 0;
  position: relative;
  vertical-align: top;
  width: 349px;
}

.list-item:nth-child(3n) {
  margin: 0 0 78px 0;
}


.list-item a {
  color: #818282;
  display: block;
  font-family: GothamBook, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  margin: 30px;
  padding-right: 25%;
}

.list-item p {
  color: #818282;
  font-family: GothamBook, sans-serif;
  font-size: 14px;
  font-weight: 400;
  margin: 0 30px;
  line-height: 17px;
}

.list-item .button {
  background: rgb(26, 64, 132);
  background: linear-gradient(90deg, rgba(26, 64, 132, 1) 0%, rgba(84, 51, 143, 1) 80%, rgba(84, 51, 143, 1) 100%);
  border-radius: 0 0 0 35px;
  border: 0px;
  height: 70px;
  border-top: 4px solid #1c75bc;
  bottom: 0px;
  color: #ffffff;
  font-family: "GothamBook", sans-serif;
  font-size: 18px;
  font-weight: bold;
  line-height: 60px;
  margin: 0;
  padding: 0 70px 0 0;
  position: absolute;
  text-align: right;
  text-decoration: none;
  width: calc(100% - 70px);
}

.list-item .button img {
  position: absolute;
  top: 14px;
  right: 15px;
}

.list-item .button.visit {
  background: rgb(21, 129, 198);
  background: linear-gradient(90deg, rgba(21, 129, 198, 1) 0%, rgba(2, 164, 226, 1) 100%);
  border-top-color: #00467e;
}

.body-list-wrap {
  display: block;
  margin: 30px auto 40px auto;
  position: relative;
  width: 1121px;
}

.body-list-item {
  background-color: #ffffff;
  border-radius: 0 35px 0 35px;
  box-shadow: 10px -5px 30px 0px rgba(0, 0, 0, 0.15);
  -moz-box-shadow: 10px -5px 30px 0px rgba(0, 0, 0, 0.15);
  -webkit-box-shadow: 10px -5px 30px 0px rgba(0, 0, 0, 0.15);
  height: 400px;
  margin: 0 32px 78px 0;
  position: relative;
  vertical-align: top;
  width: 338px;
}

.body-list-item:nth-child(3n) {
  margin: 0 0 78px 0;
}

.body-list-item div {
  color: #000000;
  display: block;
  font-family: Gotham, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  margin: 30px;
  min-height: 50px;
  padding-right: 29%;
}

.body-list-item p {
  color: #000000 !important;
  font-family: GothamBook, sans-serif;
  font-size: 14px;
  font-weight: 400;
  margin: 0 30px;
  min-height: 78px;
  line-height: 17px;
}

.body-list-item .button {
  background: rgb(26, 64, 132);
  background: linear-gradient(90deg, rgba(26, 64, 132, 1) 0%, rgba(84, 51, 143, 1) 80%, rgba(84, 51, 143, 1) 100%);
  border-radius: 0 0 0 35px;
  border: 0px;
  height: 70px;
  border-top: 4px solid #1c75bc;
  bottom: 0px;
  color: #ffffff;
  font-family: "GothamBook", sans-serif;
  font-size: 18px;
  font-weight: bold;
  line-height: 60px;
  margin: 0;
  padding: 0 70px 0 0;
  position: absolute;
  text-align: right;
  text-decoration: none;
  width: calc(100% - 70px);
}

.body-list-item .button img {
  position: absolute;
  top: 14px;
  right: 15px;
}

.body-list-item .button.visit {
  background: rgb(21, 129, 198);
  background: linear-gradient(90deg, rgba(21, 129, 198, 1) 0%, rgba(2, 164, 226, 1) 100%);
  border-top-color: #00467e;
}


.pdf-icon {
  background: url(../img/pdf-icon.png) no-repeat top right;
}

.xls-icon {
  background: url(../img/xls-icon.png) no-repeat top right;
}

.ppt-icon {
  background: url(../img/ppt-icon.png) no-repeat top right;
}

.web-icon {
  background: url(../img/web-icon.png) no-repeat top right;
}

.xls-icon {
  background: url(../img/xls-icon.png) no-repeat top right;
}

.doc-icon {
  background: url(../img/doc-icon.png) no-repeat top right;
}

.body-list-item a.no-icon {
  padding-right: 0;
}

.video-thumb {
  cursor: pointer;
  margin-top: 4px !important;
  width: 100%;
}




.steps {
  margin-top: 29px;
}

.step-item {
  border-bottom: 1px solid #6689a5;
  margin-bottom: 21px;
  padding-bottom: 23px;
}

.step-item:last-child {
  border: 0;
}

.step-circle {
  background-color: #FFFFFF;
  font-family: "Gotham", sans-serif;
  border-radius: 50px;
  color: #0f3765;
  font-size: 18px;
  font-weight: 800;
  height: 35px;
  padding: 35px 30px 30px 30px;
  width: 40px;
  text-align: center;
}

.step-item-desc-wrap {
  color: #ffffff;
  display: inherit;
  float: right;
  font-family: GothamBook, sans-serif;
  font-size: 16px;
  left: 24%;
  line-height: 20px;
  margin-left: 44px;
  width: 970px;
}

.step-title {
  font-family: Gotham, sans-serif;
  font-weight: bold;
  margin: 0 0 0 -17px;
  padding: 0;
}

.step-desc {
  display: list-item;
  font-family: GothamBook, sans-serif;
  font-weight: 400;
}

.step-desc.dash {
  list-style-type: none;
  text-indent: -9px;
  margin-left: 13px;
}

.step-desc.dash:before {
  content: "–";
  position: relative;
}

.mapping-list {
  color: #ffffff;
  font-weight: 400;
  margin-bottom: 80px;
  padding-left: 20px;
}

.mapping-list li {
  display: list-item;
  font-family: GothamBook, sans-serif;
  font-size: 16px;
  line-height: 24px;
  list-style-type: disc;
}

.body {
  display: block;
  font-family: GothamBook, sans-serif;
  font-size: 16px;
  margin: 59px auto 0 auto;
  line-height: 20px;
  width: 1121px;
}

.body p {
  margin-bottom: 25px;
  color:#000000;
}

.body-list-item p {
  margin-bottom: 0;
}

.body .references {
  display: block;
  font-family: "GothamBook", sans-serif;
  font-size: 14px;
  line-height: 21px;
  margin: 0;
  width: 1117px;
  word-break: break-word;
}

.body sup {
  font-size: 10px;
  font-weight: 400;
  position: relative;
  top: -0.2em;
  vertical-align: top;
}

.subhead {
  font-family: "Gotham", sans-serif;
  color: #000000;
  font-size: 20px;
  font-weight: bold;
}

.top-link {
  background: url(../img/icon-backToTop.png) no-repeat center right;
  bottom: 5px;
  color: #A65103;
  font-family: GothamMedium, sans-serif;
  font-size: 18px;
  position: absolute;
  right: 5px;
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  line-height: 42px;
  padding-right: 56px;
}


.site-guide,
#menuToggle,
#boton,
.home {
  display: none;
}

.footer-references span {
  word-break: break-all;
}

strong {
  font-weight: bold;
}

.video-wrapper {
  background-color: #ffffff;
  padding: 20px 48px 48px 48px;
  position: fixed;
  height: 400px;
  width: 635px;
  z-index: 11100;
  top: 10%;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  text-align: left;
}

#playVideos {
  max-width: 100%;
}

.video-wrapper .video-title {
  color: #1b4084;
  font-family: GothamBook, sans-serif;
  font-size: 30px;
  font-weight: bold;
  margin-bottom: 35px;
}

.video-wrapper .video-tag {
  width: 100%;
}

.video-wrapper .video-close {
  background: url(../img/close-video.jpeg) no-repeat center right;
  cursor: pointer;
  font-size: 0;
  height: 25px;
  margin: 7px 25px 0 0;
  position: absolute;
  right: 0;
  width: 25px;
}

.video-wrapper video {
  width: 100%;
}

.overlay {
  position: fixed;
  display: none;
  max-width: 100% !important;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 11000;
  cursor: pointer;
}

.footnote {
  font-size: 12px;
  font-weight: bold;
}

@media screen and (max-width: 1230px) {
  footer img {
    left: 78%;
  }

  .top-banner *,
  .body * {
    max-width: 95%;
  }

  .body {
    max-width: 90%;
  }

  .bottom-banner * {
    max-width: 99%;
  }

  .step-item-desc-wrap {
    max-width: 80%;
  }

  .bottom-banner,
  .list-wrap,
  .home-body {
    width: 90%;
  }

  .list-wrap {
    margin-bottom: 0;
    text-align: center;
  }

  .list-item,
  .body-list-item {
    margin: 0 37px 60px 0;
    text-align: left;
    width: 317px;
  }

  .list-item *,
  .body-list-item * {
    max-width: 100%;
  }

  .body-list-item:nth-child(3n) {
    margin: 0 37px 60px 0;
  }

  .body .references {
    margin: 0 auto;
  }

}

@media screen and (max-width: 1024px) {
  .body-list-wrap {
    margin-bottom: 50px;
    text-align: center;
    width: 90%;
  }

  .top-link {
    bottom: 0;
  }
}

@media screen and (max-width: 880px) {

  .top-banner {
    background: url(../img/banner-mobile3-bg.png) no-repeat 0 0;
    background-size: cover;
    border-radius: 0 0 0 100px;
    margin-left: 1.5%;
    padding-bottom: 46px;
    width: 98.5%;
  }

  .top-banner section {
    font-family: GothamBook, sans-serif;
    font-size: 20px;
    line-height: 30px;
    width: 86%;
  }

  .top-content p.banner-footer {
    font-size: 30px;
    margin: 24px auto;
    width: 73%;
  }

  .resources {
    margin: 80px auto 0 auto;
    width: 80%;
  }

  .resources>div {
    padding-left: 0;
    margin-right: 28px;
    margin-bottom: 70px;
    width: 297px;
  }

  .resources div:nth-child(2n) {
    margin-right: 0;
    width: 260px;
  }

  .resources div:nth-child(2n) .resource_info_wrap {
    border-right: 0;
    margin: 0 0 25px 0;
  }

  .resources .resource_info_wrap {
    margin: 0 37px 25px 0;
    padding-right: 0px;
    width: 100%;
  }

  .home-body {
    width: 87%;
  }

  .top-banner h2 {
    width: 85%;
  }

  .bottom-banner {
    padding-left: 60px;
    padding-right: 60px;
    width: calc(100% - 120px);
  }

  .step-item-desc-wrap {
    max-width: 95%;
    text-align: left;
    width: 95%;
  }

  .step-item {
    text-align: center;
  }

  .step-circle {
    margin: 60px 0 45px 0;
  }

  .list-wrap {
    padding-left: 37px;
    width: calc(100% - 37px);
  }

  .list-item {
    margin-right: 38px;
    width: 300px;
  }

  .list-item button {
    width: 300px;
    background-size: contain;
  }

  .body {
    width: 90%;
  }

  .body-list-wrap,
  .body .references,
  .body-list-item button {
    width: 100%;
  }

  .body-list-wrap {
    max-width: 100%;
    text-align: center;
    width: 100%;
    padding-bottom: 50px;
  }

  .body-list-item {
    margin-right: 32px;
    width: 300px;
  }

  .top-link {
    bottom: 35px;
    right: 13px;
  }


  .video-wrapper {
    background-color: #ffffff;
    position: fixed;
    top: 5%;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto 3%;
    padding: 20px 5% 40px 5%;
    position: fixed;
    height: 210px;
    width: 80%;
    z-index: 11100;
  }

  .video-wrapper .video-title {
    color: #1b4084;
    font-family: GothamBook, sans-serif;
    font-size: 15px;
    font-weight: bold;
    margin-bottom: 30px;
  }

  .video-wrapper .video-tag {
    width: 100%;
  }

  .video-wrapper .video-close {
    background: url(../img/close-video.jpeg) no-repeat center right;
    cursor: pointer;
    font-size: 0;
    height: 25px;
    margin: 0px 25px 0 0;
    position: absolute;
    right: 0;
    width: 25px;
  }

  .video-wrapper video {
    width: 100%;
  }

}

@media screen and (max-width: 540px) {

  .resources .resource_info_wrap {
    border-right: 0;
    margin: 0 0 25px 0;
  }

  .resources div a {
    display: block;
    height: 55px;
  }

  .resources>div button {
    width: 100%;
  }

  .resources>div,
  .resources div:nth-child(2n) {
    width: 100%;
  }

  .bottom-banner {
    padding-left: 30px;
    padding-right: 30px;
    margin-top: 38px;
    font-size: 16px;
    line-height: 18px;
    width: 85%;
  }

  .bottom-banner p {
    font-size: 14px;
    line-height: 16px;
  }

  .list-wrap {
    padding-left: 0;
    width: 100%;
  }

  .list-item {
    margin: 0 0 60px 0;
  }

  .top-banner section {
    font-size: 23px;
  }

  .step-circle {
    margin: 20px 0 40px 0;
  }

  .mapping-list li {
    font-size: 14px;
    line-height: 18px
  }

  .body {
    width: 87%;
  }

  .body-list-item,
  .body-list-item:nth-child(2n),
  .body-list-item:nth-child(3n) {
    margin: 0 0 78px 0;
  }

  .body-list-item:nth-child(3n) {
    margin: 0 0 78px 0 !important;
  }

  .body .references {
    max-width: 100%;
    word-wrap: break-word;
  }

  .top-banner {
    background-size: cover;
    padding-bottom: 10px;
  }

  .top-banner h1 {
    font-size: 19px;
    line-height: 23px;
    max-width: 85%;
    margin: 40px auto 21px auto;
  }

  .top-banner h1 span {
    display: block;
    font-size: 19px;
    line-height: 23px;
  }

  .top-banner h2 {
    font-size: 16px;
    line-height: 18px;
    width: 82%;
  }

  .top-banner section {
    font-size: 13px;
    line-height: 16px;
    margin: 20px auto 0 auto;
  }

  .top-banner section p {
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
  }

  .top-content p.banner-footer {
    font-size: 15px;
    line-height: 18px;
  }

  .subhead {
    font-size: 15px;
  }

  .body p {
    font-size: 14px;
  }

  .body p sup {
    font-size: 8px;
    position: relative;
    top: -0.2em;
    vertical-align: top;
  }

  .video-thumb img {
    width: 83%;
  }

}

/* new styles sergio */

@media screen and (max-width: 880px) {

  .top-banner header .logo img {
    width: 60%;
  }

}

@media screen and (max-width: 540px) {

  .video-wrapper .video-title {
    margin-bottom: 10px;
  }

  .menu a {
    font-size: 12px;
    padding-left: 60px;
  }

  @supports (-webkit-touch-callout: none) {
    .filter-list span {
      line-height: 32px;
    }

    .filter-content .apply {
      line-height: 70px;
    }

    .top-link {
      padding-top: 12px;
    }

  }
}

@media screen and (orientation:portrait) {
  .video-wrapper {
    top: 10%;
  }
}

@media screen and (orientation:landscape) and (max-height: 736px) and (max-width: 1000px) {
  .video-wrapper {
    max-height: 100%;
    margin: 0 auto;
    height: 300px;
    width: 480px;
    top: 25%;
  }
}

@media screen and (orientation:landscape) and (max-height: 360px) {
  .video-wrapper {
    max-height: 100%;
    margin: 0 auto;
    height: 250px;
    width: 391px;
    top: 0%;
  }
}

/*Tag Sup*/
.top-banner sup {
  font-size: 11px;
  font-weight: 400;
  margin: 0 0 0 2px;
  vertical-align: text-bottom;
}

.bottom-banner sup {
  font-size: 11px;
  font-weight: 400;
  margin: 0 0 0 2px;
  vertical-align: 4px;
  line-height: 1;
}

.bottom-banner sup.sup-title {
  vertical-align: 7px;
  line-height: 1;
}

@media screen and (max-width: 880px) {
  .top-banner sup {
    vertical-align: 5px;
    line-height: 1;
  }
}

@media screen and (max-width: 540px) {

  .top-banner sup,
  .bottom-banner sup {
    font-size: 8px;
    line-height: 1;
    vertical-align: 4px;
  }

  .bottom-banner sup.sup-title {
    vertical-align: 5px;
  }
}

/*.TagSup*/
</style>



// WEBPACK FOOTER //
// src/components/Expanding_Internal_Awareness.vue